/* eslint-disable no-undef */
import { useState, useEffect } from 'react';
import { createInvite, type InviteServiceError } from '../../services/invite.service';

export interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  currentUserId: string;
  onInviteCreated?: (_token: string, _email: string) => void;
}

export function InviteModal({
  isOpen,
  onClose,
  orgId,
  currentUserId,
  onInviteCreated,
}: InviteModalProps) {
  const [email, setEmail] = useState('');
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setInviteToken(null);
      setError(null);
    }
  }, [isOpen]);

  // Focus trap + Escape
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await createInvite(orgId, email, currentUserId);
      setInviteToken(token);
      onInviteCreated?.(token, email);
    } catch (err) {
      const serviceError = err as InviteServiceError;
      setError(serviceError.message || 'Failed to create invite');
    } finally {
      setIsLoading(false);
    }
  }

  function handleCopyLink() {
    if (!inviteToken) return;
    const link = `${window.location.origin}/invite/${inviteToken}`;
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(link);
    } else {
      // Fallback for environments without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = link;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }

  if (!isOpen) return null;

  const inviteLink = inviteToken ? `${window.location.origin}/invite/${inviteToken}` : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 sm:p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Invite Member"
        className={[
          'flex w-full flex-col bg-white',
          'fixed bottom-0 rounded-t-2xl animate-slide-up-sheet',
          'sm:static sm:max-w-md sm:rounded-xl sm:shadow-xl sm:animate-none',
        ].join(' ')}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 className="font-display text-base font-semibold text-green-900">
            Invite Member
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
          >
            <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 2l12 12M14 2L2 14" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">
          {/* Hint */}
          <div className="flex gap-3 rounded-lg bg-amber-50 p-3">
            <span aria-hidden="true" className="shrink-0 text-lg">💡</span>
            <p className="text-sm text-gray-700">
              An invite link will be generated. Share it via WhatsApp or email. It expires in <strong>3 days</strong> and the invitee joins as <strong>Operator</strong> by default.
            </p>
          </div>

          {!inviteToken ? (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              {/* Email input */}
              <div className="flex flex-col gap-1">
                <label htmlFor="invite-email" className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Invitee Email <span className="text-red-600">*</span>
                </label>
                <input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(null); }}
                  placeholder="e.g. sara@example.com"
                  disabled={isLoading}
                  className="rounded-md border border-gray-300 px-3 py-2 font-body text-sm text-gray-900 placeholder-gray-400 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
                <p className="text-xs text-gray-500">
                  The invite link will only work if the recipient signs in with this exact email.
                </p>
              </div>

              {/* Error message */}
              {error && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading}
                className="rounded-md bg-green-700 px-4 py-2 font-body text-sm font-semibold text-white hover:bg-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? 'Generating...' : 'Generate Invite Link'}
              </button>
            </form>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Success message */}
              <div className="flex items-center gap-2 text-sm font-semibold text-green-800">
                <span aria-hidden="true">✓</span>
                <span>Invite link generated</span>
              </div>

              {/* Invite link box */}
              <div className="flex items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2">
                <code className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-xs text-gray-700">
                  {inviteLink}
                </code>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="shrink-0 rounded bg-green-700 px-3 py-1 font-body text-xs font-semibold text-white hover:bg-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
                >
                  Copy
                </button>
              </div>

              {/* Info text */}
              <p className="text-xs text-gray-500">
                Share this link directly with the invitee. Do not share it publicly.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-2 border-t border-gray-100 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="order-last rounded-md px-4 py-2 font-body text-sm text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 disabled:cursor-not-allowed disabled:opacity-50 sm:order-first"
          >
            {inviteToken ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}
